import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { paths } from 'routes/paths';
import LoadingSpinner from 'components/LoadingSpinner';
import EmptyTableState from 'components/EmptyTableState';
import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';
import MapIllustration from 'assets/properties-map-icon.svg';
import NoApplicationsFoundIcon from 'assets/no_applications_found.svg';
import PlusIcon from 'assets/plus.svg?react';
import { useStore } from 'components/Store';
import useOperationEvents from 'screens/Applications&WorkOrders/Hooks/useOperationEvents';
import { closeNotification } from 'screens/Applications&WorkOrders/Components/Notification/genericNotification';
import { CREATE_CLASSNAME_PREFIX as APPLICATION_CREATE_CLASSNAME_PREFIX } from 'screens/Applications&WorkOrders/Applications/Components/Notification';
import { CREATE_CLASSNAME_PREFIX as WORK_ORDER_CREATE_CLASSNAME_PREFIX } from 'screens/Applications&WorkOrders/WorkOrders/Components/Notification';
import useSelectedCropSeasonIds from 'hooks/useSelectedCropSeasonIds';
import { buildLandingPage } from '../helpers';
import {
  SummaryParamsProvider,
  useSummaryParams
} from '../../Context/SummaryParamsContext';
import { OperationSortProvider } from '../../Context/SortContext';
import { OperationType } from '../../RouteAdmin';
import OperationListView from '../OperationListView';
import { ApplicationSummary } from '../../Applications/Hooks/useApplication';
import { OperationSummaryError, OperationSummaryParams } from '../types';
import useDestroyRecordSourceMessageOnNavigation from './useDestroyRecordSourceMessageOnNavigation';

export interface OperationsPageConfig {
  icon: React.ReactNode;
  title: string;
  breadcrumbTitle: string;
  breadcrumbValue: string;
  searchPlaceholder: string;
  emptyMessage: string;
  emptyStateTitle: string;
  emptyStateMessage: string;
  createButtonLabel: string;
  cropZoneMessage: string;
  operationType: OperationType;
  createPath: string;
  storageKey: string;
}

interface OperationsPageProps {
  config: OperationsPageConfig;
  items: ApplicationSummary[];
  loading: boolean;
  totalElements: number;
  pageSize: number;
  currentPage: number;
  onItemDeleted: () => void;
}

interface UseSummaryHookReturn {
  loading: boolean;
  applications?: ApplicationSummary[];
  workOrders?: ApplicationSummary[];
  totalElements: number;
  pageSize: number;
  currentPage: number;
  loadApplications?: () => Promise<void>;
  loadWorkOrders?: () => Promise<void>;
  errors?: OperationSummaryError | null;
}

type UseSummaryHook = (params?: OperationSummaryParams) => UseSummaryHookReturn;

const OperationsPage: React.FC<OperationsPageProps> = ({
  config,
  items,
  loading,
  totalElements,
  pageSize,
  currentPage,
  onItemDeleted
}) => {
  const { hasSearchTerm, hasFilters } = useSummaryParams();
  const history = useHistory();
  const {
    sendAccessEventOnDirectNavigation,
    sendInitiateCreateEvent
  } = useOperationEvents(config.operationType);
  const {
    state: { loadingOrg, isGlobalCropSeasonLoading, cropSeasons }
  } = useStore();
  const hasItems = items.length > 0;
  const hasCompletedInitialLoad = useRef(false);
  const { selectedCropSeasonIds } = useSelectedCropSeasonIds();
  const hasCropZonesInCropSeasons = useMemo(
    () =>
      (
        (cropSeasons as {
          id: string;
          cropZoneIds?: string[];
        }[]) || []
      ).some(
        cropSeason =>
          selectedCropSeasonIds.includes(cropSeason.id) &&
          (cropSeason.cropZoneIds?.length ?? 0) > 0
      ),
    [cropSeasons, selectedCropSeasonIds]
  );

  useEffect(() => {
    sendAccessEventOnDirectNavigation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading) {
      hasCompletedInitialLoad.current = true;
    }
  }, [loading]);

  const renderContent = () => {
    if (loadingOrg || isGlobalCropSeasonLoading) {
      return (
        <div className="flex items-center justify-center flex-1">
          <LoadingSpinner
            message="Loading..."
            className="flex items-center justify-center h-19 w-60 py-8 px-4 rounded-lg bg-white shadow-md"
          />
        </div>
      );
    }

    if (!hasCropZonesInCropSeasons || selectedCropSeasonIds.length === 0) {
      return (
        <div className="pt-4 px-4">
          <div className="bg-white shadow-sm-card rounded-lg">
            <EmptyTableState
              title="No Crop Zones Found"
              message={config.cropZoneMessage}
              overlayIcon={MapIllustration}
              button={
                <Button
                  type={ButtonType.primary}
                  size="large"
                  onClick={() => history.push(paths.properties)}
                >
                  Go to Properties
                </Button>
              }
            />
          </div>
        </div>
      );
    }

    const createNewOperation = () => {
      sendInitiateCreateEvent();

      closeNotification(
        config.operationType === OperationType.APPLICATION
          ? APPLICATION_CREATE_CLASSNAME_PREFIX
          : WORK_ORDER_CREATE_CLASSNAME_PREFIX
      );
      history.push(`${config.createPath}/create`);
    };

    if (
      hasCompletedInitialLoad.current &&
      !loading &&
      !hasItems &&
      !hasSearchTerm &&
      !hasFilters
    ) {
      return (
        <div className="pt-4 px-4">
          <div className="bg-white shadow-sm-card rounded-lg">
            <EmptyTableState
              title={config.emptyStateTitle}
              message={config.emptyStateMessage}
              overlayIcon={NoApplicationsFoundIcon}
              button={
                <Button
                  type={ButtonType.primary}
                  size="large"
                  icon={<PlusIcon />}
                  onClick={createNewOperation}
                >
                  {config.createButtonLabel}
                </Button>
              }
            />
          </div>
        </div>
      );
    }

    return (
      <OperationListView
        items={items}
        totalElements={totalElements}
        currentPage={currentPage}
        pageSize={pageSize}
        isLoading={loading}
        hasFilters={hasFilters}
        onItemDeleted={onItemDeleted}
        config={{
          icon: config.icon,
          title: config.title,
          searchPlaceholder: config.searchPlaceholder,
          emptyMessage: config.emptyMessage,
          operationType: config.operationType,
          onCreateClick: createNewOperation
        }}
      />
    );
  };

  return buildLandingPage(renderContent(), {
    breadcrumbItems: [
      {
        title: config.breadcrumbTitle,
        value: config.breadcrumbValue,
        isLast: true
      }
    ]
  });
};

interface OperationsPageWrapperProps {
  config: OperationsPageConfig;
  useSummaryHook: UseSummaryHook;
}

const OperationsPageWrapper: React.FC<OperationsPageWrapperProps> = ({
  config,
  useSummaryHook
}) => {
  const { params } = useSummaryParams();
  const summaryResult = useSummaryHook(params);

  const getItems = useMemo(() => {
    if (summaryResult.loading || summaryResult.errors) {
      return [];
    }

    return config.operationType === OperationType.WORK_ORDER
      ? summaryResult.workOrders ?? []
      : summaryResult.applications ?? [];
  }, [
    summaryResult.loading,
    summaryResult.errors,
    summaryResult.workOrders,
    summaryResult.applications,
    config.operationType
  ]);

  const items = getItems;

  const loadItems = useMemo(
    () =>
      config.operationType === OperationType.WORK_ORDER
        ? summaryResult.loadWorkOrders ?? (async () => {})
        : summaryResult.loadApplications ?? (async () => {}),
    [
      config.operationType,
      summaryResult.loadApplications,
      summaryResult.loadWorkOrders
    ]
  );

  const handleItemDeleted = useCallback(() => {
    loadItems();
  }, [loadItems]);

  return (
    <OperationsPage
      config={config}
      items={items}
      loading={summaryResult.loading}
      totalElements={summaryResult.totalElements}
      pageSize={summaryResult.pageSize}
      currentPage={summaryResult.currentPage}
      onItemDeleted={handleItemDeleted}
    />
  );
};

interface OperationsPageProviderProps {
  config: OperationsPageConfig;
  useSummaryHook: UseSummaryHook;
}

export const OperationsPageProvider: React.FC<OperationsPageProviderProps> = ({
  config,
  useSummaryHook
}) => {
  useDestroyRecordSourceMessageOnNavigation({
    enabled: !!config.operationType,
    pathname:
      config.operationType === OperationType.WORK_ORDER
        ? paths.workOrders
        : paths.applications
  });

  return (
    <SummaryParamsProvider>
      <OperationSortProvider storageKey={config.storageKey}>
        <OperationsPageWrapper
          config={config}
          useSummaryHook={useSummaryHook}
        />
      </OperationSortProvider>
    </SummaryParamsProvider>
  );
};

export default OperationsPage;
